import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../../SectionBuilder/SectionContainer';

import css from './FaqSection.module.css';

/**
 * Predefined FAQ categories mapping to block IDs specified for this marketplace:
 * - general-faq       -> GENERAL / General
 * - renting-faq       -> RENTING / Renting
 * - selling-faq       -> LENDING & SELLING / Lending & Selling
 * - pricing-faq       -> PRICING & FEES / Pricing & Fees
 * - payment-faq       -> PAYMENTS & PROTECTION / Payments & Protection
 * - shipping-faq      -> SHIPPING & DELIVERY / Shipping & Delivery
 */
export const FAQ_CATEGORIES = [
  {
    id: 'general-faq',
    labelKey: 'FaqSection.pillGeneral',
    defaultLabel: 'GENERAL',
    headingKey: 'FaqSection.categoryGeneral',
    defaultHeading: 'General',
  },
  {
    id: 'renting-faq',
    labelKey: 'FaqSection.pillRenting',
    defaultLabel: 'RENTING',
    headingKey: 'FaqSection.categoryRenting',
    defaultHeading: 'Renting',
  },
  {
    id: 'selling-faq',
    labelKey: 'FaqSection.pillSelling',
    defaultLabel: 'LENDING & SELLING',
    headingKey: 'FaqSection.categorySelling',
    defaultHeading: 'Lending & Selling',
  },
  {
    id: 'pricing-faq',
    labelKey: 'FaqSection.pillPricing',
    defaultLabel: 'PRICING & FEES',
    headingKey: 'FaqSection.categoryPricing',
    defaultHeading: 'Pricing & Fees',
  },
  {
    id: 'payment-faq',
    labelKey: 'FaqSection.pillPayment',
    defaultLabel: 'PAYMENTS & PROTECTION',
    headingKey: 'FaqSection.categoryPayment',
    defaultHeading: 'Payments & Protection',
  },
  {
    id: 'shipping-faq',
    labelKey: 'FaqSection.pillShipping',
    defaultLabel: 'SHIPPING & DELIVERY',
    headingKey: 'FaqSection.categoryShipping',
    defaultHeading: 'Shipping & Delivery',
  },
];

/**
 * Fallback questions displayed if CMS blocks are not yet populated.
 * Uses exact questions from the editorial design reference.
 */
const DEFAULT_FAQ_BLOCKS = [
  // General
  {
    blockId: 'general-faq',
    title: { fieldType: 'heading2', content: 'How do members communicate?' },
    text: {
      fieldType: 'paragraph',
      content:
        'Members can communicate directly through our secure in-app messaging system once an inquiry or reservation request is initiated.',
    },
  },
  {
    blockId: 'general-faq',
    title: { fieldType: 'heading2', content: 'Is Dear Danielle a peer-to-peer marketplace?' },
    text: {
      fieldType: 'paragraph',
      content:
        'Yes, Dear Danielle is a curated peer-to-peer wardrobe sharing and rental marketplace where lenders and borrowers connect directly.',
    },
  },
  {
    blockId: 'general-faq',
    title: { fieldType: 'heading2', content: 'What is Dear Danielle?' },
    text: {
      fieldType: 'paragraph',
      content:
        'Dear Danielle is a luxury fashion rental and resale community that empowers members to monetize exceptional style and access iconic designer wardrobes.',
    },
  },
  {
    blockId: 'general-faq',
    title: { fieldType: 'heading2', content: 'Where is Dear Danielle available?' },
    text: {
      fieldType: 'paragraph',
      content:
        'Dear Danielle is available nationwide across the United States, offering tracked shipping and convenient local pickup in select metro areas.',
    },
  },
  {
    blockId: 'general-faq',
    title: { fieldType: 'heading2', content: "Can I use Dear Danielle if I'm not in Alabama?" },
    text: {
      fieldType: 'paragraph',
      content:
        'Absolutely! We support nationwide insured shipping so members can rent, lend, and sell anywhere in the United States.',
    },
  },
  // Renting
  {
    blockId: 'renting-faq',
    title: { fieldType: 'heading2', content: 'How does renting work?' },
    text: {
      fieldType: 'paragraph',
      content:
        'Browse our curated closets, select your event or wear dates, and submit a booking request. Once accepted, the item is delivered directly to you with a prepaid return packaging slip included.',
    },
  },
  {
    blockId: 'renting-faq',
    title: { fieldType: 'heading2', content: "What if my rental doesn't fit?" },
    text: {
      fieldType: 'paragraph',
      content:
        'If an item does not fit, notify our team within 24 hours of delivery and postmark the return with security tags intact to receive full rental credit.',
    },
  },
  {
    blockId: 'renting-faq',
    title: { fieldType: 'heading2', content: 'Can I rent for one day?' },
    text: {
      fieldType: 'paragraph',
      content:
        'Our standard rental reservations start at 4 days to ensure seamless delivery and styling, though custom rental timeframes can be coordinated with the lender.',
    },
  },
  // Lending & Selling
  {
    blockId: 'selling-faq',
    title: { fieldType: 'heading2', content: 'How do I list an item for lending or sale?' },
    text: {
      fieldType: 'paragraph',
      content:
        'Listing takes only a few minutes. Take high-resolution photos, set your rental or purchase price, choose your availability dates, and publish your closet.',
    },
  },
  // Pricing & Fees
  {
    blockId: 'pricing-faq',
    title: { fieldType: 'heading2', content: 'What are the platform and service fees?' },
    text: {
      fieldType: 'paragraph',
      content:
        'A transparent service fee is applied to confirmed bookings to support lender protection insurance, secure Stripe processing, and dedicated 24/7 member support.',
    },
  },
  // Payments & Protection
  {
    blockId: 'payment-faq',
    title: { fieldType: 'heading2', content: 'Are my items protected against accidental damage?' },
    text: {
      fieldType: 'paragraph',
      content:
        'Yes. Every rental is covered by our comprehensive lender protection policy covering professional cleaning, minor repairs, and full replacement value in the event of total loss.',
    },
  },
  // Shipping & Delivery
  {
    blockId: 'shipping-faq',
    title: { fieldType: 'heading2', content: 'How does shipping and return delivery work?' },
    text: {
      fieldType: 'paragraph',
      content:
        'Every order includes tracked two-way shipping with a reusable mailer and prepaid return shipping label. Simply place the garment inside and drop it off at the designated carrier.',
    },
  },
];

/**
 * Determines which category a block belongs to based on blockId matching.
 */
const getCategoryForBlock = (block, categories) => {
  const blockId = (block?.blockId || '').trim().toLowerCase();
  if (!blockId) return categories[0].id;

  // 1. Direct match (e.g. 'general-faq')
  const exact = categories.find(cat => cat.id.toLowerCase() === blockId);
  if (exact) return exact.id;

  // 2. Prefix match (e.g. 'general-faq-1', 'renting-faq_2')
  const prefix = categories.find(
    cat =>
      blockId.startsWith(`${cat.id.toLowerCase()}-`) ||
      blockId.startsWith(`${cat.id.toLowerCase()}_`)
  );
  if (prefix) return prefix.id;

  // 3. Shorthand match without '-faq' (e.g. 'general', 'renting', 'selling')
  const base = categories.find(cat => {
    const raw = cat.id.replace('-faq', '').toLowerCase();
    return blockId === raw || blockId.startsWith(`${raw}-`) || blockId.startsWith(`${raw}_`);
  });
  if (base) return base.id;

  // Fallback to first category
  return categories[0].id;
};

/**
 * Group blocks into category arrays.
 */
const groupBlocksByCategory = (blocks, categories) => {
  const grouped = {};
  categories.forEach(cat => {
    grouped[cat.id] = [];
  });

  (blocks || []).forEach(block => {
    const catId = getCategoryForBlock(block, categories);
    if (!grouped[catId]) {
      grouped[catId] = [];
    }
    grouped[catId].push(block);
  });

  return grouped;
};

/**
 * FaqSection component
 *
 * PageBuilder custom section for FAQ pages with sticky category navigation,
 * pill category filters, and smooth animated expandable question cards.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.sectionId id of the section (defaults to 'faq-section')
 * @param {Object?} props.title heading from CMS
 * @param {Object?} props.description description from CMS
 * @param {Array<Object>?} props.blocks FAQ block items from CMS
 * @param {Object?} props.appearance appearance styling
 * @param {Object?} props.options PageBuilder options
 * @returns {JSX.Element}
 */
const FaqSection = props => {
  const {
    sectionId = 'faq-section',
    className,
    rootClassName,
    title,
    description,
    blocks = [],
    appearance,
    options = {},
  } = props;

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  // Use provided CMS blocks or fallback to default blocks
  const effectiveBlocks = blocks && blocks.length > 0 ? blocks : DEFAULT_FAQ_BLOCKS;
  const blocksByCategory = groupBlocksByCategory(effectiveBlocks, FAQ_CATEGORIES);

  // Filter to categories that contain at least one question
  const visibleCategories = FAQ_CATEGORIES.filter(
    cat => blocksByCategory[cat.id] && blocksByCategory[cat.id].length > 0
  );

  const [activeCategory, setActiveCategory] = useState(visibleCategories[0]?.id || 'general-faq');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const isScrollingByClick = useRef(false);

  // Scroll spy via IntersectionObserver to dynamically highlight category pill
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      entries => {
        if (isScrollingByClick.current) return;

        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const catId = entry.target.id?.replace('faq-cat-', '');
            if (catId) {
              setActiveCategory(catId);
            }
          }
        });
      },
      {
        rootMargin: '-10% 0px -70% 0px',
        threshold: 0,
      }
    );

    visibleCategories.forEach(cat => {
      const el = document.getElementById(`faq-cat-${cat.id}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [visibleCategories]);

  const handleCategoryClick = categoryId => {
    setActiveCategory(categoryId);
    isScrollingByClick.current = true;

    const element = document.getElementById(`faq-cat-${categoryId}`);
    if (element) {
      const topOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - topOffset;

      if (typeof window.scrollTo === 'function') {
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }

      // Clear click lock after scroll completes
      setTimeout(() => {
        isScrollingByClick.current = false;
      }, 700);
    } else {
      isScrollingByClick.current = false;
    }
  };

  const toggleFaq = itemKey => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  };

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(css.root, className)}
      rootClassName={rootClassName || css.sectionContainerRoot}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.container}>
        {/* Left Column (Sticky Sidebar) */}
        <aside className={css.sidebar}>
          <header className={css.header}>
            {hasDataInFields([title], fieldOptions) ? (
              <Field data={title} className={css.title} options={fieldOptions} />
            ) : (
              <h1 className={css.title}>
                <FormattedMessage id="FaqSection.heading" defaultMessage="All FAQs" />
              </h1>
            )}

            {hasDataInFields([description], fieldOptions) ? (
              <div className={css.description}>
                <Field data={description} options={fieldOptions} />
              </div>
            ) : null}
          </header>

          {/* Category Navigation Pills */}
          {visibleCategories.length > 0 && (
            <nav className={css.categoryContainer} aria-label="FAQ Categories">
              {visibleCategories.map(category => {
                const isActive = activeCategory === category.id;
                return (
                  <button
                    key={category.id}
                    type="button"
                    className={classNames(css.categoryButton, { [css.active]: isActive })}
                    onClick={() => handleCategoryClick(category.id)}
                    aria-pressed={isActive}
                  >
                    <FormattedMessage
                      id={category.labelKey}
                      defaultMessage={category.defaultLabel}
                    />
                  </button>
                );
              })}
            </nav>
          )}
        </aside>

        {/* Right Column (Category Groups & Accordion Cards) */}
        <div className={css.faqContent}>
          {visibleCategories.map(category => {
            const categoryBlocks = blocksByCategory[category.id] || [];
            if (categoryBlocks.length === 0) return null;

            return (
              <section
                key={category.id}
                id={`faq-cat-${category.id}`}
                className={css.categoryGroup}
              >
                <h2 className={css.categoryHeading}>
                  <FormattedMessage
                    id={category.headingKey}
                    defaultMessage={category.defaultHeading}
                  />
                </h2>

                <div className={css.faqList}>
                  {categoryBlocks.map((block, blockIndex) => {
                    const { title: blockTitle, text } = block;
                    const hasTitle =
                      typeof blockTitle === 'string'
                        ? blockTitle.trim().length > 0
                        : hasDataInFields([blockTitle], fieldOptions);
                    const hasText =
                      typeof text === 'string'
                        ? text.trim().length > 0
                        : hasDataInFields([text], fieldOptions);

                    if (!hasTitle) return null;

                    const itemKey = `${category.id}-${blockIndex}`;
                    const isExpanded = expandedItems.has(itemKey);

                    return (
                      <article
                        key={itemKey}
                        className={classNames(css.faqItem, { [css.expanded]: isExpanded })}
                      >
                        <button
                          type="button"
                          className={css.faqQuestion}
                          onClick={() => toggleFaq(itemKey)}
                          aria-expanded={isExpanded}
                          aria-controls={`faq-answer-${itemKey}`}
                        >
                          <span className={css.questionText}>
                            {typeof blockTitle === 'string' ? (
                              blockTitle
                            ) : (
                              <Field data={blockTitle} options={fieldOptions} />
                            )}
                          </span>
                          <span className={css.expandIcon} aria-hidden="true">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 5V19.002"
                                stroke="black"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M19.002 12.002H5"
                                stroke="black"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </button>

                        {hasText ? (
                          <div
                            className={css.faqAnswerWrapper}
                            id={`faq-answer-${itemKey}`}
                            role="region"
                          >
                            <div className={css.faqAnswerInner}>
                              <div className={css.faqAnswerContent}>
                                {typeof text === 'string' ? (
                                  <p>{text}</p>
                                ) : (
                                  <Field data={text} options={fieldOptions} />
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </SectionContainer>
  );
};

export default FaqSection;
